// invest_FE/src/services/announcementService.js
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';

// --- Public ---

/**
 * Fetches the currently active announcement for public display.
 * @returns {Promise<{_id: string, message: string, countdownTarget?: string, backgroundColor?: string} | null>} The active announcement or null.
 */
export const getActiveAnnouncement = async () => {
  try {
    const response = await apiFetch(`${BASE_URL}/api/announcements/active`);
    if (!response.ok) {
      console.error('Failed to fetch active announcement');
      return null;
    }
    return response.json(); // Will be the announcement object or null from the backend
  } catch (error) {
    console.error('Network error fetching announcement:', error);
    return null;
  }
};

// --- Admin ---

/**
 * Fetches all announcements for the admin panel.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<Array<object>>} A list of all announcements.
 */
export const getAllAnnouncements = async (token) => {
  const response = await apiFetch(`${BASE_URL}/api/announcements`, {}, token);
  if (!response.ok) {
    throw new Error('Failed to fetch announcements');
  }
  return response.json();
};

/**
 * Creates a new announcement.
 * @param {{title: string, message: string, countdownTarget?: string, backgroundColor?: string}} data - The announcement data.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The created announcement.
 */
export const createAnnouncement = async (data, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/announcements`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
    token
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to create announcement' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Updates an existing announcement.
 * @param {string} id - The ID of the announcement to update.
 * @param {{title: string, message: string, countdownTarget?: string, backgroundColor?: string}} data - The updated data.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The updated announcement.
 */
export const updateAnnouncement = async (id, data, token) => {
  const response = await apiFetch(
    `${BASE_URL}/api/announcements/${id}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    },
    token
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to update announcement' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Deletes an announcement.
 * @param {string} id - The ID of the announcement to delete.
 * @param {string} token - The admin user's auth token.
 */
export const deleteAnnouncement = async (id, token) => {
  const response = await apiFetch(`${BASE_URL}/api/announcements/${id}`, { method: 'DELETE' }, token);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to delete announcement' }));
    throw new Error(errorData.message);
  }
  // No content on successful deletion
};

/**
 * Sets a specific announcement as the active one.
 * @param {string} id - The ID of the announcement to activate.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The activated announcement.
 */
export const setActiveAnnouncement = async (id, token) => {
  const response = await apiFetch(`${BASE_URL}/api/announcements/${id}/set-active`, { method: 'POST' }, token);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to set active announcement' }));
    throw new Error(errorData.message);
  }
  return response.json();
};

/**
 * Deactivates any currently active announcement.
 * @param {string} token - The admin user's auth token.
 * @returns {Promise<object>} The success message from the server.
 */
export const deactivateCurrentAnnouncement = async (token) => {
  const response = await apiFetch(`${BASE_URL}/api/announcements/deactivate`, { method: 'POST' }, token);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to deactivate announcement' }));
    throw new Error(errorData.message);
  }
  return response.json();
};
